import { getData } from '../utils/data.js';
import { normalizeKana } from '../utils/japaneseText.js';
export const BUYBACK_MINIMUM_PRICE = 1000;
export const BUYBACK_MAXIMUM_PRICE = 20000;
export const BUYBACK_SHIPPING_DEADLINE_DAYS = 14;
export const BUYBACK_SHIPPING_REMINDER_DAYS = 3;
export const BUYBACK_MAX_NEEDS_FIX_COUNT = 3;
export const BUYBACK_MIN_AGE = 18;
export const BUYBACK_MAX_ITEMS = 30;
export const DeviceBuybackStatus = {
    Applied: 'applied',
    NeedsFix: 'needsFix',
    Approved: 'approved',
    Expired: 'expired',
    Received: 'received',
    Assessed: 'assessed',
    Paid: 'paid',
    Rejected: 'rejected',
    Returned: 'returned',
    Canceled: 'canceled',
};
export const textDeviceBuybackStatus = (v) => {
    switch (v) {
        case DeviceBuybackStatus.Applied:
            return '本人確認中';
        case DeviceBuybackStatus.NeedsFix:
            return '書類の再提出が必要';
        case DeviceBuybackStatus.Approved:
            return '発送待ち';
        case DeviceBuybackStatus.Expired:
            return '発送期限切れ';
        case DeviceBuybackStatus.Received:
            return '到着・査定中';
        case DeviceBuybackStatus.Assessed:
            return '査定完了・振込待ち';
        case DeviceBuybackStatus.Paid:
            return '振込完了';
        case DeviceBuybackStatus.Rejected:
            return '却下';
        case DeviceBuybackStatus.Returned:
            return '返送';
        case DeviceBuybackStatus.Canceled:
            return 'キャンセル';
    }
};
export const ACTIVE_DEVICE_BUYBACK_STATUSES = [
    DeviceBuybackStatus.Applied,
    DeviceBuybackStatus.NeedsFix,
    DeviceBuybackStatus.Approved,
    DeviceBuybackStatus.Received,
    DeviceBuybackStatus.Assessed,
];
export const CANCELABLE_DEVICE_BUYBACK_STATUSES = [
    DeviceBuybackStatus.Applied,
    DeviceBuybackStatus.NeedsFix,
    DeviceBuybackStatus.Approved,
];
export const IdentificationKind = {
    DriversLicense: 'driversLicense',
    MyNumberCard: 'myNumberCard',
    Passport: 'passport',
    ResidenceCard: 'residenceCard',
};
export const IdentificationSide = {
    Front: 'front',
    Back: 'back',
};
const SIDES_FRONT_AND_BACK = Object.freeze([IdentificationSide.Front, IdentificationSide.Back]);
const SIDES_FRONT_ONLY = Object.freeze([IdentificationSide.Front]);
export const requiredIdentificationSides = (kind) => {
    switch (kind) {
        case IdentificationKind.DriversLicense:
        case IdentificationKind.ResidenceCard:
            return SIDES_FRONT_AND_BACK;
        case IdentificationKind.MyNumberCard:
        case IdentificationKind.Passport:
            return SIDES_FRONT_ONLY;
    }
};
export const textIdentificationSide = (kind, side) => {
    if (kind === IdentificationKind.Passport)
        return '顔写真のページ';
    return side === IdentificationSide.Front ? '表面' : '裏面';
};
export const MY_NUMBER_BACK_NOTICE = 'マイナンバーカードは表面のみをご提出ください。裏面には個人番号が記載されており、法令により収集できません。';
export const textIdentificationKind = (v) => {
    switch (v) {
        case IdentificationKind.DriversLicense:
            return '運転免許証';
        case IdentificationKind.MyNumberCard:
            return 'マイナンバーカード';
        case IdentificationKind.Passport:
            return 'パスポート';
        case IdentificationKind.ResidenceCard:
            return '在留カード';
    }
};
export const IdentificationMethod = {
    ImageAndBankTransfer: 'imageAndBankTransfer',
};
export const IdentificationMatchField = {
    Name: 'name',
    NameKana: 'nameKana',
    Address: 'address',
    Birthday: 'birthday',
    AccountHolder: 'accountHolder',
    NotExpired: 'notExpired',
};
export const textIdentificationMatchField = (v) => {
    switch (v) {
        case IdentificationMatchField.Name:
            return '氏名';
        case IdentificationMatchField.NameKana:
            return '氏名 (カナ)';
        case IdentificationMatchField.Address:
            return '住所';
        case IdentificationMatchField.Birthday:
            return '生年月日';
        case IdentificationMatchField.AccountHolder:
            return '口座名義';
        case IdentificationMatchField.NotExpired:
            return '書類が有効期限内';
    }
};
export const MatchResult = {
    Matched: 'matched',
    Mismatched: 'mismatched',
    Unreadable: 'unreadable',
};
export const isAccountHolderMatchingSeller = (nameKana, accountHolderKana) => {
    const a = normalizeKana(nameKana ?? '');
    const b = normalizeKana(accountHolderKana ?? '');
    return a !== '' && a === b;
};
export const BankAccountType = {
    Ordinary: 'ordinary',
    Checking: 'checking',
};
export const deviceBuybackConverter = {
    fromFirestore(snap, op) {
        return getData(snap, op);
    },
    toFirestore(d) {
        return d;
    },
};
export const calcAge = (birthday, at) => {
    const [y, m, d] = birthday.split('-').map(Number);
    let age = at.getFullYear() - y;
    const month = at.getMonth() + 1;
    if (month < m || (month === m && at.getDate() < d)) {
        age -= 1;
    }
    return age;
};
export const isUnderAge = (birthday, at) => calcAge(birthday, at) < BUYBACK_MIN_AGE;
export const isValidAssessedPrice = (price) => Number.isInteger(price) && price >= BUYBACK_MINIMUM_PRICE && price <= BUYBACK_MAXIMUM_PRICE;
export const isFullyAssessed = (items) => items.length > 0 && items.every((v) => v.assessedPrice != null);
export const calcTotalPrice = (items) => isFullyAssessed(items) ? items.reduce((sum, v) => sum + (v.assessedPrice ?? 0), 0) : null;
export const calcShippingDeadline = (approvedAt) => {
    const deadline = new Date(approvedAt);
    deadline.setDate(deadline.getDate() + BUYBACK_SHIPPING_DEADLINE_DAYS);
    return deadline;
};
export const deviceBuybackCollection = (clientId) => `clients/${clientId}/deviceBuybacks`;
export const deviceBuybackPath = (clientId, buybackId) => `${deviceBuybackCollection(clientId)}/${buybackId}`;
export const buybackIdentificationDir = (clientId, buybackId) => `buybacks/${clientId}/${buybackId}/identification`;
//# sourceMappingURL=deviceBuyback.js.map